import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Key, Eye, EyeOff } from "lucide-react";

export default function ApiKeysPage() {
  const [keys, setKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [selectedWs, setSelectedWs] = useState<string>("");
  const [showKey, setShowKey] = useState<number | null>(null);

  useEffect(() => {
    api.get("/api/v1/workspaces").then((ws) => {
      setWorkspaces(ws);
      if (ws.length > 0) setSelectedWs(ws[0].id.toString());
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedWs) return;
    api.get(`/api/v1/api-keys?workspaceId=${selectedWs}`).then(setKeys).catch(() => {});
  }, [selectedWs]);

  const handleCreate = async () => {
    const label = prompt("API key label:");
    if (!label || !selectedWs) return;
    try {
      const result = await api.post("/api/v1/api-keys", { workspaceId: parseInt(selectedWs), label });
      setKeys([{ id: result.id, label, key_hash: result.key, created_at: new Date().toISOString(), _fullKey: result.key }, ...keys]);
      toast.success("API key created! Copy it now — it won't be shown again.");
    } catch (e: any) { toast.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Revoke this API key?")) return;
    try { await api.delete(`/api/v1/api-keys/${id}`); setKeys(keys.filter(k => k.id !== id)); toast.success("Revoked"); }
    catch (e: any) { toast.error(e.message); }
  };

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success("API key copied!");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">API Keys</h1>
        <div className="flex items-center gap-3">
          {workspaces.length > 0 && (
            <select value={selectedWs} onChange={(e) => setSelectedWs(e.target.value)} className="px-3 py-2 rounded-lg border border-input bg-background text-sm">
              {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
          <button onClick={handleCreate} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Key
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">{[1,2].map(i=><div key={i} className="h-16 bg-muted rounded-xl"/>)}</div>
      ) : keys.length === 0 ? (
        <div className="text-center py-12">
          <Key className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium">No API keys yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create an API key to access the calendar REST API programmatically.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map(k => (
            <div key={k.id} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
              <Key className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{k.label}</p>
                <p className="text-xs text-muted-foreground font-mono">
                  {showKey === k.id ? (k._fullKey || k.key_hash || "****") : "••••••••••••"}
                </p>
              </div>
              <button onClick={() => setShowKey(showKey === k.id ? null : k.id)} className="p-2 rounded-lg hover:bg-accent text-muted-foreground">
                {showKey === k.id ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              {k._fullKey && <button onClick={() => copyKey(k._fullKey)} className="p-2 rounded-lg hover:bg-accent text-muted-foreground"><Copy className="w-4 h-4" /></button>}
              <button onClick={() => handleDelete(k.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
